import { InjectionToken, NgModule } from '@angular/core';
import { ActivatedRouteSnapshot, RouterModule, Routes } from '@angular/router';
import { LandingComponent } from './landing/landing.component';
import { SignUpComponent } from './auth/sign-up/sign-up.component';
import { PageNotFoundComponent } from './common/page-not-found/page-not-found.component';
import { SignUpConfirmComponent } from './auth/sign-up-confirm/sign-up-confirm.component';
import { ContestsAvailableListComponent } from './contest/contests-available-list/contests-available-list.component';
import { TeachersListComponent } from './teacher/teachers-list/teachers-list.component';
import { StudentsListComponent } from './student/students-list/students-list.component';
import { AuthGuard } from './helper/auth.guard';
import { CompetitionsListComponent } from './competition/competitions-list/competitions-list.component';
import { SignInComponent } from './auth/sign-in/sign-in.component';
import { HostsListComponent } from './host/hosts-list/hosts-list.component';
import { ProfileDetailsComponent } from './profile/profile-details/profile-details.component';
import { ProfileComponent } from './profile/profile/profile.component';
import { RoleSelectComponent } from './auth/role-select/role-select.component';
import { CompetitionDetailsComponent } from './competition/competition-details/competition-details.component';
import { ProfileSchoolDetailsComponent } from './profile/profile-school-details/profile-school-details.component';
import { HelpComponent } from './support/help/help.component';
import { PassResetComponent } from './auth/pass-reset/pass-reset.component';
import { PassResetConfirmComponent } from './auth/pass-reset-confirm/pass-reset-confirm.component';
import { ContestsHistoryListComponent } from './contest/contests-history-list/contests-history-list.component';
import { SchoolModifyDetailsComponent } from './profile/school-modify-details/school-modify-details.component';
import { ServiceTermsComponent } from './support/service-terms/service-terms.component';
import { ServicePrivacyComponent } from './support/service-privacy/service-privacy.component';
import { ContestPaymentRedirectComponent } from './contest/contest-payment-redirect/contest-payment-redirect.component';
import { CoordinatorsListComponent } from './coordinator/coordinators-list/coordinators-list.component';
import { EnrolStatsComponent } from './stats/enrol/enrol-stats/enrol-stats.component';
import { ContestsManageListComponent } from './contest/contests-manage-list/contests-manage-list.component';
import { ContestTasksEditorComponent } from './contest-task/contest-tasks-editor/contest-tasks-editor.component';
import { ContestExamCenterComponent } from './contest-exam/contest-exam-center/contest-exam-center.component';
import { CanDeactivateGuard } from './helper/can-deactivate.guard';
import { ExamCardStatsComponent } from './stats/exam-card/exam-card-stats/exam-card-stats.component';
import { ExamRatingStatsComponent } from './stats/exam/exam-rating-stats/exam-rating-stats.component';
import { StatsDashboardComponent } from './stats/stats-dasboard/stats-dashboard.component';
import { ExamResultsStatsComponent } from './stats/exam/exam-results/exam-results-stats.component';
import { TeacherResultsStatsComponent } from './stats/teacher/teacher-results/teacher-results-stats.component';
import { ExamSchoolResultsStatsComponent } from './stats/exam/exam-school-results/exam-school-results-stats.component';
import { PrintableReportStatsComponent } from './stats/print/printable-report/printable-report-stats.component';
import {
  ExamRegionSchoolsResultStatsComponent
} from './stats/exam/exam-region-schools-result/exam-region-schools-result-stats.component';
import { MigrationsDashboardComponent } from './migration/migrations-dashboard/migrations-dashboard.component';
import { SettingsDashboardComponent } from './settings/settings-dasboard/settings-dashboard.component';
import { SchoolsManageComponent } from './settings/school-manage/schools-manage.component';
import {
  ContestParticipationStatsComponent
} from './stats/contest/contest-participation-stats/contest-participation-stats.component';

const externalUrlProvider = new InjectionToken('externalUrlRedirectResolver');

const routes: Routes = [
  {path: '', component: LandingComponent},

  {
    path: 'sign-in', component: SignInComponent, data: {
      title: $localize`:Navigation|Sign In title@@navigation-sign-in-title:Sign In`
    }
  },
  // {path: 'sign-in-sso', component: SignInSsoComponent, data: {title: 'Social Sign In'}},
  {
    path: 'sign-up/:role', component: SignUpComponent, data: {
      title: $localize`:Navigation|Sign Up title@@navigation-sign-up-title:Sign Up`
    }
  },
  {
    path: 'sign-up-confirm/:email/:username', component: SignUpConfirmComponent, data: {
      title: $localize`:Navigation|Sign Up Confirm title@@navigation-sign-up-confirm-title:Sign Up Confirm`
    }
  },
  {
    path: 'pass-reset', component: PassResetComponent, data: {
      title: $localize`:Navigation|Password Reset title@@navigation-password-reset-title:Password Reset`
    }
  },
  {
    path: 'pass-reset-confirm/:email/:username', component: PassResetConfirmComponent, data: {
      title: $localize`:Navigation|Password Reset Confirm title@@navigation-password-reset-confirm-title:Password Reset Confirm`
    }
  },
  {
    path: 'role-select', component: RoleSelectComponent, data: {
      title: $localize`:Navigation|Select Role title@@navigation-select-role-title:Select Role`
    }
  },
  {
    path: 'school-select', component: SchoolModifyDetailsComponent, data: {
      title: $localize`:Navigation|Select School title@@navigation-select-school-title:Select School`
    }
  },

  {
    path: 'profile', component: ProfileComponent, canActivate: [AuthGuard], data: {
      title: $localize`:Navigation|Profile title@@navigation-profile-title:Profile`
    }, children: [
      {
        path: 'details', component: ProfileDetailsComponent, canActivate: [AuthGuard], data: {
          title: $localize`:Navigation|Profile Details title@@navigation-profile-details-title:Profile Details`
        }
      },
      {
        path: 'school', component: ProfileSchoolDetailsComponent, canActivate: [AuthGuard], data: {
          title: $localize`:Navigation|Profile School Details title@@navigation-profile-school-details-title:School Details`
        }
      },
    ]
  },

  {
    path: 'invite/confirm', component: SignUpComponent, data: {
      title: $localize`:Navigation|Sign Up Confirm title@@navigation-sign-up-confirm-title:Sign Up Confirm`
    }
  },
  {
    path: 'invite/request', component: SignUpComponent, data: {
      title: $localize`:Navigation|Sign Up Confirm title@@navigation-sign-up-confirm-title:Sign Up Confirm`
    }
  },

  {
    path: 'contests', component: ContestsManageListComponent, canActivate: [AuthGuard], data: {
      title: $localize`:Navigation|Contests title@@navigation-contests-title:Contests`
    }
  },
  {
    path: 'contests/:contestId', component: ContestTasksEditorComponent, canActivate: [AuthGuard], data: {
      title: $localize`:Navigation|Contest Tasks Editor title@@navigation-contest-tasks-editor-title:Tasks Editor`
    }
  },
  {
    path: 'contests/exam/:contestId',
    component: ContestExamCenterComponent,
    canActivate: [AuthGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      title: $localize`:Navigation|Contest Exam Center title@@navigation-contest-exam-center-title:Contest Exam Center`
    }
  },
  {
    path: 'contests-history', component: ContestsHistoryListComponent, canActivate: [AuthGuard], data: {
      title: $localize`:Navigation|Contests title@@navigation-contests-title:Contests`
    }
  },
  {
    path: 'contests-available', component: ContestsAvailableListComponent, canActivate: [AuthGuard], data: {
      title: $localize`:Navigation|Available Contests title@@navigation-available-contests-title:Available Contests`
    }
  },
  {
    path: 'payment/confirm/:contestId/:role/:userId',
    component: ContestPaymentRedirectComponent,
    canActivate: [AuthGuard],
    data: {
      title: $localize`:Navigation|Contests Payment Confirm title@@navigation-contests-payment-confirm-title:Contests Payment Confirm`
    }
  },
  {
    path: 'payment/confirm-group/:contestId/:groupId',
    component: ContestPaymentRedirectComponent,
    canActivate: [AuthGuard],
    data: {
      title: $localize`:Navigation|Contests Payment Confirm title@@navigation-contests-payment-confirm-title:Contests Payment Confirm`
    }
  },

  {
    path: 'competitions', component: CompetitionsListComponent, canActivate: [AuthGuard], data: {
      title: $localize`:Navigation|Competitions title@@navigation-competitions-title:Competitions`
    }
  },
  {
    path: 'competitions/details', component: CompetitionDetailsComponent, canActivate: [AuthGuard], data: {
      title: $localize`:Navigation|Competitions title@@navigation-competition-details-title:Competition Details`
    }
  },

  {
    path: 'coordinators', component: CoordinatorsListComponent, canActivate: [AuthGuard], data: {
      title: $localize`:Navigation|Coordinators title@@navigation-coordinators-title:Coordinators`
    }
  },

  {
    path: 'teachers', component: TeachersListComponent, canActivate: [AuthGuard], data: {
      title: $localize`:Navigation|Teachers title@@navigation-teachers-title:Teachers`
    }
  },

  {
    path: 'students', component: StudentsListComponent, canActivate: [AuthGuard], data: {
      title: $localize`:Navigation|Students title@@navigation-students-title:Students`
    }
  },

  {
    path: 'hosts', component: HostsListComponent, canActivate: [AuthGuard], data: {
      title: $localize`:Navigation|Hosts title@@navigation-hosts-title:Hosts`
    }
  },

  {
    path: 'migrations', component: MigrationsDashboardComponent, canActivate: [AuthGuard], data: {
      title: $localize`:Navigation|Migrations title@@navigation-migrations-title:Migrations`
    }
  },

  {
    path: 'stats', component: StatsDashboardComponent, canActivate: [AuthGuard], data: {
      title: $localize`:Navigation|Stats Dashboard title@@navigation-stats-dashboard-title:Stats Dashboard`
    }, children: [
      {
        path: 'enrol', component: EnrolStatsComponent, canActivate: [AuthGuard], data: {
          title: $localize`:Navigation|Enrol Stats title@@navigation-enrol-stats-title:Enrol Stats`
        }
      },
      {
        path: 'results', component: ExamResultsStatsComponent, canActivate: [AuthGuard], data: {
          title: $localize`:Navigation|Contest results title@@navigation-contest-results-stats-title:Contest Results`
        }
      },
      {
        path: 'contest', component: ContestParticipationStatsComponent, canActivate: [AuthGuard], data: {
          title: $localize`:Navigation|Contest participation stats title@@navigation-contest-participation-stats-title:Contest Stats`
        }
      },
      {
        path: 'teachers', component: TeacherResultsStatsComponent, canActivate: [AuthGuard], data: {
          title: $localize`:Navigation|Teacher results title@@navigation-teacher-results-stats-title:Teacher Results`
        }
      },
      {
        path: 'printable', component: PrintableReportStatsComponent, canActivate: [AuthGuard], data: {
          title: $localize`:Navigation|Printable reports title@@navigation-printable-reports-title:Printable Reports`
        }
      },
    ]
  },

  {
    path: 'stats/exam-card/:contestId/:userId', component: ExamCardStatsComponent, canActivate: [AuthGuard], data: {
      title: $localize`:Navigation|Exam Card Stats title@@navigation-exam-card-stats-title:Answer Sheet`,
    }
  },
  {
    path: 'stats/rating', component: ExamRatingStatsComponent, canActivate: [AuthGuard], data: {
      title: $localize`:Navigation|Exam Rating Stats title@@navigation-exam-rating-stats-title:Contest Rating`
    }
  },
  {
    path: 'stats/rating/school/:contestId/:regionId',
    component: ExamSchoolResultsStatsComponent,
    canActivate: [AuthGuard],
    data: {
      title: $localize`:Navigation|Contest school results title@@navigation-contest-school-results-stats-title:Contest School Results`
    }
  },
  {
    path: 'stats/exam/region/:contestId/:regionId',
    component: ExamRegionSchoolsResultStatsComponent,
    canActivate: [AuthGuard],
    data: {
      title: $localize`:Navigation|Contest region schools result title@@navigation-contest-region-schools-result-stats-title:Contest Region Schools Result`
    }
  },

  {
    path: 'settings', component: SettingsDashboardComponent, canActivate: [AuthGuard], data: {
      title: $localize`:Navigation|Settings title@@navigation-settings-dashboard-title:Settings`
    }, children: [
      {
        path: 'schools', component: SchoolsManageComponent, canActivate: [AuthGuard], data: {
          title: $localize`:Navigation|Schools Manage title@@navigation-schools-manage-title:Manage Schools`
        }
      },
    ]
  },

  {
    path: 'support', component: HelpComponent, data: {
      title: $localize`:Navigation|Support title@@navigation-support-title:Support`
    }
  },
  {
    path: 'terms', component: ServiceTermsComponent, data: {
      title: $localize`:Navigation|Service User Agreement title@@navigation-service-terms-title:Service User Agreement`
    }
  },
  {
    path: 'privacy', component: ServicePrivacyComponent, data: {
      title: $localize`:Navigation|Service Privacy Policy title@@navigation-service-privacy-title:Service Privacy Policy`
    }
  },

  {path: '**', component: PageNotFoundComponent}
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
  providers: [
    {
      provide: externalUrlProvider,
      useValue: (route: ActivatedRouteSnapshot) => {
        window.open(route.data.url, route.data.target);
      },
    },
  ],
})
export class AppRoutingModule {
}
